import React, { useState, useRef, useEffect } from 'react';
import { Button } from './button';
import { Card, CardHeader, CardTitle, CardContent } from './card';
import { Eye, EyeOff, Key, Shield, AlertCircle, CheckCircle, Settings, X, Edit2, ChevronDown, ChevronUp, HelpCircle, Lock, Clock, Trash2, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Separator } from './separator';

interface FloatingApiKeyManagerProps {
  value?: string;
  onChange: (key: string) => void;
  isRequired: boolean;
  isValidating?: boolean;
  validationError?: string;
  isOpen: boolean;
  onToggle: () => void;
}

// Validate API key format (client-side only for UX)
const validateKeyFormat = (key: string): boolean => {
  // Basic OpenAI API key validation - supports both old and new project keys
  // Old keys: ~51 chars, New project keys (sk-proj-): 160+ chars
  return key.startsWith('sk-') && key.length >= 20 && key.length <= 200;
};

export const FloatingApiKeyManager: React.FC<FloatingApiKeyManagerProps> = ({
  value = '',
  onChange,
  isRequired,
  isValidating = false,
  validationError,
  isOpen,
  onToggle
}) => {
  const [showKey, setShowKey] = useState(false);
  const [showSecurityInfo, setShowSecurityInfo] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Derive state from props
  const apiKey = value;
  const isValid = validateKeyFormat(apiKey);
  const hasKey = apiKey.length > 0;

  // SECURITY: Clear clipboard on component unmount
  useEffect(() => {
    return () => {
      if (navigator.clipboard && apiKey) {
        navigator.clipboard.writeText('').catch(() => {
          // Ignore clipboard clear errors
        });
      }
    };
  }, [apiKey]);

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newKey = e.target.value;
    onChange(newKey);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleClearKey = () => {
    onChange('');
    setShowKey(false);
  };

  const toggleShowKey = () => {
    setShowKey(!showKey);
  };

  // SECURITY: Prevent autocomplete, autofill, and browser saving
  const securityProps = {
    autoComplete: 'off',
    autoCorrect: 'off',
    autoCapitalize: 'off',
    spellCheck: false,
    'data-lpignore': 'true',
    'data-form-type': 'other',
    'data-1p-ignore': 'true',
  };

  const getValidationIcon = () => {
    if (validationError) {
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
    if (isValidating) {
      return (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Shield className="w-4 h-4 text-blue-500" />
        </motion.div>
      );
    }
    if (isValid) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <Key className="w-4 h-4 text-gray-400" />;
  };

  const getStatusMessage = () => {
    if (validationError) {
      return (
        <motion.p 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-red-600 dark:text-red-400 mt-2"
        >
          {validationError}
        </motion.p>
      );
    }
    if (isValidating) {
      return (
        <motion.p 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-blue-600 dark:text-blue-400 mt-2"
        >
          Validating API key...
        </motion.p>
      );
    }
    if (isValid) {
      return (
        <motion.p 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-green-600 dark:text-green-400 mt-2"
        >
          API key format is valid
        </motion.p>
      );
    }
    if (apiKey && !isValid) {
      return (
        <motion.p 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-orange-600 dark:text-orange-400 mt-2"
        >
          Please enter a valid OpenAI API key (starts with sk-)
        </motion.p>
      );
    }
    return null;
  };

  return (
    <>
      {/* Floating Card */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
              onClick={onToggle}
            />
            
            {/* Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 flex items-center justify-center z-50 p-4"
            >
              <div className="w-full max-w-md">
                <Card className="border bg-white dark:bg-gray-800 shadow-2xl border-gray-200 dark:border-gray-600">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center space-x-2 text-lg text-gray-900 dark:text-gray-100">
                      <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <span>OpenAI API Key</span>
                      {isRequired && (
                        <span className="text-sm font-normal text-amber-600 dark:text-amber-400">
                          (Required)
                        </span>
                      )}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onToggle}
                      className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Security Notice */}
                  <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <Shield className="w-4 h-4 inline mr-1" />
                      Your API key is secure: Never stored, only kept in memory during your session
                    </p>
                  </div>

                  {/* Input Field */}
                  <div className="relative">
                    <div className="relative">
                      <input
                        ref={inputRef}
                        type={showKey ? 'text' : 'password'}
                        value={apiKey}
                        onChange={handleKeyChange}
                        onKeyDown={handleKeyDown}
                        placeholder="sk-..."
                        className={`w-full px-4 py-3 pr-20 border-2 rounded-lg transition-all duration-200 font-mono text-sm
                          ${isValid 
                            ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-950/20' 
                            : validationError 
                              ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                          } 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                          placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100`}
                        {...securityProps}
                      />
                      
                      {/* Show/Hide Button */}
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                        {getValidationIcon()}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={toggleShowKey}
                          className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                          disabled={!apiKey}
                        >
                          {showKey ? (
                            <EyeOff className="w-4 h-4 text-gray-500" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-500" />
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Status Message */}
                    <AnimatePresence>
                      {getStatusMessage()}
                    </AnimatePresence>
                  </div>

                  {/* Action Buttons */}
                  {hasKey && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex justify-between items-center"
                    >
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleClearKey}
                        className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-950/20"
                      >
                        Clear Key
                      </Button>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Key will be cleared when you refresh the page
                      </p>
                    </motion.div>
                  )}

                  {/* Required Notice */}
                  {isRequired && !hasKey && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3"
                    >
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        <AlertCircle className="w-4 h-4 inline mr-1" />
                        An OpenAI API key is required to proceed. Get yours from{' '}
                        <a 
                          href="https://platform.openai.com/api-keys" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline hover:text-amber-600 dark:hover:text-amber-300"
                        >
                          OpenAI Platform
                        </a>
                      </p>
                    </motion.div>
                  )}

                  {/* Security Info Toggle */}
                  <Separator className="my-2" />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowSecurityInfo(!showSecurityInfo)}
                    className="w-full justify-between text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <span className="flex items-center">
                      <HelpCircle className="w-4 h-4 mr-2" />
                      API Key Security Information
                    </span>
                    {showSecurityInfo ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>

                  {/* Security FAQ Content */}
                  <AnimatePresence>
                    {showSecurityInfo && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-3 space-y-3 max-h-96 overflow-y-auto"
                      >
                        {/* How is my key stored? */}
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                          <h4 className="flex items-center text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            <Lock className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                            How is my API key stored?
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            Your API key is <strong>never stored permanently</strong>. It exists only in your browser's memory during your current session. 
                            We use secure, encrypted session storage that automatically clears when you close the tab or refresh the page.
                          </p>
                        </div>

                        {/* How long does it last? */}
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                          <h4 className="flex items-center text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            <Clock className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                            How long does my key last?
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            Your API key remains active only for your current browser session. It will be automatically deleted when you:
                          </p>
                          <ul className="text-xs text-gray-600 dark:text-gray-400 mt-2 ml-4 space-y-1">
                            <li>• Refresh the page</li>
                            <li>• Close the browser tab</li>
                            <li>• Navigate away from the site</li>
                            <li>• Clear it manually using the "Clear Key" button</li>
                          </ul>
                        </div>

                        {/* Who can see my key? */}
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                          <h4 className="flex items-center text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            <Shield className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" />
                            Who can see my API key?
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            <strong>Only you</strong> can see your API key. It is:
                          </p>
                          <ul className="text-xs text-gray-600 dark:text-gray-400 mt-2 ml-4 space-y-1">
                            <li>• Never sent to any external servers except OpenAI</li>
                            <li>• Never logged or recorded in our systems</li>
                            <li>• Never visible to other users or administrators</li>
                            <li>• Encrypted in transit using HTTPS</li>
                          </ul>
                        </div>

                        {/* How is it used? */}
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                          <h4 className="flex items-center text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            <Key className="w-4 h-4 mr-2 text-orange-600 dark:text-orange-400" />
                            How is my key used?
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            Your API key is used exclusively to authenticate requests to OpenAI's API. Each research query you make 
                            is sent directly to OpenAI using your key, ensuring you have full control over your API usage and billing.
                          </p>
                        </div>

                        {/* Can I revoke it? */}
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                          <h4 className="flex items-center text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            <RefreshCw className="w-4 h-4 mr-2 text-red-600 dark:text-red-400" />
                            Can I revoke my API key?
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            Yes! You have complete control:
                          </p>
                          <ul className="text-xs text-gray-600 dark:text-gray-400 mt-2 ml-4 space-y-1">
                            <li>• Clear it instantly from this app using the "Clear Key" button</li>
                            <li>• Revoke it permanently from your <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-800 dark:hover:text-gray-200">OpenAI dashboard</a></li>
                            <li>• Generate a new key anytime if you suspect it's been compromised</li>
                          </ul>
                        </div>

                        {/* Best Practices */}
                        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                          <h4 className="flex items-center text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                            <Shield className="w-4 h-4 mr-2" />
                            Security Best Practices
                          </h4>
                          <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
                            <li>✓ Use a dedicated API key for this application</li>
                            <li>✓ Set usage limits in your OpenAI account</li>
                            <li>✓ Regularly rotate your API keys</li>
                            <li>✓ Never share your API key with others</li>
                            <li>✓ Monitor your usage in the OpenAI dashboard</li>
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};